/* ─── Addon Detail Modal ─── */
class AddonDetailModal extends HTMLElement {
  constructor() {
    super();
    this._productCache = {};
    this._currentVariantId = null;
    this._productData = null;
    this._images = [];
    this._currentImageIndex = 0;
    this._isOpen = false;
    this._isLoading = false;
  }

  open(productId) {
    if (this._isLoading || this._isOpen) return;
    this._isLoading = true;

    this.fetchProduct(productId).then(product => {
      this._isLoading = false;
      if (!product || this._isOpen) return;
      this._productData = product;
      this._images = product.images || [];
      this._currentImageIndex = 0;
      this._isOpen = true;
      this.renderModal(product);
      this._bindEvents();
      document.body.classList.add('addon-modal-open');
    });
  }

  fetchProduct(productId) {
    if (this._productCache[productId]) {
      return Promise.resolve(this._productCache[productId]);
    }
    return fetch(`/products/${productId}.js`)
      .then(r => r.json())
      .then(product => {
        this._productCache[productId] = product;
        return product;
      })
      .catch(() => null);
  }

  renderModal(product) {
    const tpl = this.querySelector('template');
    if (!tpl) return;

    const clone = tpl.content.cloneNode(true);
    const modal = clone.querySelector('.addon-modal');

    modal.querySelector('.addon-modal__title').textContent = product.title;

    this._renderImages(modal);

    const defaultVariant = product.variants[0];
    this._currentVariantId = defaultVariant.id;
    this._updatePrice(modal, defaultVariant);

    const desc = modal.querySelector('.addon-modal__description');
    if (product.description) {
      desc.innerHTML = product.description;
      // Add "Read more" toggle if description is long
      requestAnimationFrame(() => {
        if (desc.scrollHeight > desc.clientHeight + 2) {
          const link = document.createElement('span');
          link.className = 'addon-modal__read-more';
          link.textContent = 'Read more';
          link.addEventListener('click', () => {
            const expanded = desc.classList.toggle('is-expanded');
            link.textContent = expanded ? 'Show less' : 'Read more';
          });
          desc.parentElement.appendChild(link);
        }
      });
    }

    if (product.variants.length > 1) {
      this._renderVariants(modal, product);
    }

    document.body.appendChild(clone);
    this._modal = document.body.querySelector('.addon-modal:last-of-type');
  }

  _renderImages(modal) {
    const mainImg = modal.querySelector('.addon-modal__main-image');
    const thumbsContainer = modal.querySelector('.addon-modal__thumbs');
    const prevArrow = modal.querySelector('.addon-modal__img-arrow--prev');
    const nextArrow = modal.querySelector('.addon-modal__img-arrow--next');

    if (this._images.length === 0) return;

    mainImg.src = this._images[0];
    mainImg.alt = this._productData.title;

    if (this._images.length > 1) {
      this._images.forEach((src, i) => {
        const thumb = document.createElement('button');
        thumb.type = 'button';
        thumb.className = 'addon-modal__thumb' + (i === 0 ? ' is-active' : '');
        const thumbSrc = src.replace(/(\.\w+)(\?.*)?$/, '_120x$1');
        thumb.innerHTML = `<img src="${thumbSrc}" alt="">`;
        thumb.addEventListener('click', () => this._showImage(i));
        thumbsContainer.appendChild(thumb);
      });
      thumbsContainer.style.display = 'flex';
    }

    this._updateArrows(prevArrow, nextArrow);
  }

  _showImage(index) {
    if (index < 0 || index >= this._images.length) return;
    this._currentImageIndex = index;

    const mainImg = this._modal.querySelector('.addon-modal__main-image');
    mainImg.src = this._images[index];

    this._modal.querySelectorAll('.addon-modal__thumb').forEach((t, i) => {
      t.classList.toggle('is-active', i === index);
    });

    this._updateArrows(
      this._modal.querySelector('.addon-modal__img-arrow--prev'),
      this._modal.querySelector('.addon-modal__img-arrow--next')
    );
  }

  _updateArrows(prevArrow, nextArrow) {
    if (!prevArrow || !nextArrow) return;
    const hasMultiple = this._images.length > 1;
    prevArrow.style.display = hasMultiple ? '' : 'none';
    nextArrow.style.display = hasMultiple ? '' : 'none';
    prevArrow.disabled = this._currentImageIndex === 0;
    nextArrow.disabled = this._currentImageIndex === this._images.length - 1;
  }

  _renderVariants(modal, product) {
    const container = modal.querySelector('.addon-modal__variants');
    const defaultVariant = product.variants[0];

    const select = document.createElement('select');
    select.className = 'addon-modal__variant-select';

    product.variants.forEach(v => {
      const opt = document.createElement('option');
      opt.value = v.id;
      opt.textContent = v.title;
      opt.dataset.price = v.price;
      opt.dataset.comparePrice = v.compare_at_price || '';
      opt.dataset.available = v.available;
      opt.dataset.imageIndex = this._getVariantImageIndex(v);
      if (!v.available) {
        opt.textContent += ' - Sold out';
        opt.disabled = true;
      }
      if (v.id === defaultVariant.id) opt.selected = true;
      select.appendChild(opt);
    });

    select.addEventListener('change', () => {
      const opt = select.options[select.selectedIndex];
      this._currentVariantId = parseInt(opt.value);
      this._updatePrice(modal, {
        price: parseInt(opt.dataset.price),
        compare_at_price: opt.dataset.comparePrice ? parseInt(opt.dataset.comparePrice) : null
      });
      const imgIdx = parseInt(opt.dataset.imageIndex);
      if (!isNaN(imgIdx) && imgIdx >= 0) {
        this._showImage(imgIdx);
      }
    });

    container.appendChild(select);
  }

  _getVariantImageIndex(variant) {
    if (!variant.featured_image) return -1;
    const variantSrc = variant.featured_image.src;
    return this._images.findIndex(src => {
      const base = src.split('?')[0].replace(/_\d+x\./, '.');
      const vBase = variantSrc.split('?')[0].replace(/_\d+x\./, '.');
      return base === vBase || src === variantSrc;
    });
  }

  _updatePrice(modal, variant) {
    const priceContainer = modal.querySelector('.addon-modal__price');
    const currencyFormat = window.theme?.settings?.money_with_currency_format || '${{amount}}';
    const price = formatMoney(variant.price, currencyFormat);
    const hasSale = variant.compare_at_price && variant.compare_at_price > variant.price;

    if (hasSale) {
      const compare = formatMoney(variant.compare_at_price, currencyFormat);
      priceContainer.innerHTML = `<span class="addon-card__price-sale">${price}</span><s class="addon-card__price-compare">${compare}</s>`;
    } else {
      priceContainer.innerHTML = `<span class="addon-card__price-regular">${price}</span>`;
    }
  }

  _bindEvents() {
    if (!this._modal) return;

    this._modal.querySelector('.addon-modal__backdrop').addEventListener('click', () => this.close());
    this._modal.querySelector('.addon-modal__close').addEventListener('click', () => this.close());

    // Close on click outside dialog (catches edge cases)
    this._modal.addEventListener('click', (e) => {
      if (e.target === this._modal) this.close();
    });

    this._modal.querySelector('.addon-modal__atc-btn').addEventListener('click', () => {
      this._addToCart();
    });

    const prevArrow = this._modal.querySelector('.addon-modal__img-arrow--prev');
    const nextArrow = this._modal.querySelector('.addon-modal__img-arrow--next');
    if (prevArrow) prevArrow.addEventListener('click', () => this._showImage(this._currentImageIndex - 1));
    if (nextArrow) nextArrow.addEventListener('click', () => this._showImage(this._currentImageIndex + 1));

    // Keyboard: Escape to close, arrow keys for gallery
    this._onKeyDown = (e) => {
      if (e.key === 'Escape') this.close();
      if (e.key === 'ArrowLeft') this._showImage(this._currentImageIndex - 1);
      if (e.key === 'ArrowRight') this._showImage(this._currentImageIndex + 1);
    };
    document.addEventListener('keydown', this._onKeyDown);

    // Touch swipe on main image
    this._bindSwipe();

    requestAnimationFrame(() => {
      this._modal.querySelector('.addon-modal__dialog').classList.add('is-open');
    });
  }

  _bindSwipe() {
    const wrap = this._modal.querySelector('.addon-modal__main-image-wrap');
    if (!wrap) return;

    let startX = 0;
    let startY = 0;

    wrap.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });

    wrap.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) this._showImage(this._currentImageIndex + 1);
        else this._showImage(this._currentImageIndex - 1);
      }
    }, { passive: true });
  }

  _addToCart() {
    const btn = this._modal.querySelector('.addon-modal__atc-btn');
    btn.classList.add('is-loading');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', this._currentVariantId);
    formData.append('quantity', 1);

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    if (cart) {
      cart.getSectionsToRender().forEach(s => formData.append('sections[]', s.id));
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement(document.activeElement);
    }

    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then(r => r.json())
      .then(response => {
        btn.classList.remove('is-loading');

        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'addon-modal',
            productVariantId: this._currentVariantId,
            errors: response.errors || response.description,
            message: response.message,
          });
          return;
        }

        this.dispatchEvent(new CustomEvent('addon:modal_atc', {
          bubbles: true,
          detail: {
            productId: this._productData?.id ? String(this._productData.id) : null,
            variantId: String(this._currentVariantId),
            productTitle: this._productData?.title || null,
            price: response.items?.[0]?.price ? (response.items[0].price / 100).toFixed(2) : null,
            category: this.closest('[data-category]')?.dataset?.category,
          },
        }));

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'addon-modal',
          productVariantId: this._currentVariantId,
          cartData: response,
        });

        if (cart) cart.renderContents(response);
        this.close();
      })
      .catch(() => {
        btn.classList.remove('is-loading');
      });
  }

  close() {
    if (!this._modal || !this._isOpen) return;
    this._isOpen = false;

    if (this._onKeyDown) {
      document.removeEventListener('keydown', this._onKeyDown);
      this._onKeyDown = null;
    }
    this._modal.querySelector('.addon-modal__dialog').classList.remove('is-open');
    setTimeout(() => {
      if (this._modal) {
        this._modal.remove();
        this._modal = null;
      }
      this._productData = null;
      this._currentVariantId = null;
      this._images = [];
      this._currentImageIndex = 0;
      document.body.classList.remove('addon-modal-open');
    }, 250);
  }
}

if (!customElements.get('addon-detail-modal')) {
  customElements.define('addon-detail-modal', AddonDetailModal);
}

class UpsellAddOn extends HTMLElement {
  constructor() {
    super();
    this.variantImages = {};
    this._unsubVariantChange = null;
  }

  connectedCallback() {
    this.init();
    this.subscribeVariantChange();
  }

  disconnectedCallback() {
    if (this._unsubVariantChange) this._unsubVariantChange();
  }

  init() {
    this.querySelectorAll('.addon-card__variant-select').forEach(select => {
      const productId = select.dataset.productId;
      const card = select.closest('.addon-card');
      const imageJson = card.querySelector('.variant-images-json');

      if (imageJson) {
        try {
          this.variantImages[productId] = JSON.parse(imageJson.textContent);
        } catch (e) {
          console.error('Error parsing variant images JSON:', e);
        }
      }

      select.addEventListener('change', (e) => {
        e.stopPropagation();
        this.updatePrice(e.target);
        this.updateImage(e.target);
        this.updateQuickAddVariant(e.target);
      });
      select.addEventListener('click', (e) => e.stopPropagation());
    });

    this.querySelectorAll('.addon-card__checkbox').forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        const card = e.target.closest('.addon-card');
        if (card) {
          card.classList.toggle('addon-card--selected', e.target.checked);
          card.dispatchEvent(new CustomEvent('addon:checkbox_toggle', {
            bubbles: true,
            detail: {
              productId: card.dataset.productId,
              productTitle: card.querySelector('.addon-card__title')?.textContent?.trim(),
              checked: e.target.checked,
              category: card.closest('[data-category]')?.dataset?.category,
            },
          }));
        }
      });
    });

    this.querySelectorAll('.addon-card').forEach(card => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('.addon-card__checkbox') ||
            e.target.closest('.addon-card__check') ||
            e.target.closest('.addon-card__variant-select') ||
            e.target.closest('.addon-card__quick-add')) return;

        const modal = card.querySelector('addon-detail-modal');
        const productId = card.dataset.handle || card.dataset.productId;
        if (modal && productId) {
          card.dispatchEvent(new CustomEvent('addon:click', {
            bubbles: true,
            detail: {
              productId: card.dataset.productId,
              handle: card.dataset.handle,
              productTitle: card.querySelector('.addon-card__title')?.textContent?.trim(),
              category: card.closest('[data-category]')?.dataset?.category,
            },
          }));
          modal.open(productId);
        }
      });
    });

    this.initScrollArrows();
  }

  initScrollArrows() {
    const wrap = this.closest('.addon-group__scroll-wrap');
    if (!wrap) return;

    const track = this;
    const prevBtn = wrap.querySelector('.addon-group__arrow--prev');
    const nextBtn = wrap.querySelector('.addon-group__arrow--next');
    if (!prevBtn || !nextBtn) return;

    const scrollAmount = 200;

    const updateArrows = () => {
      const { scrollLeft, scrollWidth, clientWidth } = track;
      prevBtn.classList.toggle('is-hidden', scrollLeft <= 5);
      nextBtn.classList.toggle('is-hidden', scrollLeft + clientWidth >= scrollWidth - 5);
    };

    prevBtn.addEventListener('click', () => {
      track.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    });

    nextBtn.addEventListener('click', () => {
      track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    });

    track.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);

    // Initial check after layout
    requestAnimationFrame(updateArrows);
  }

  updateQuickAddVariant(selectElement) {
    const card = selectElement.closest('.addon-card');
    const quickAdd = card.querySelector('quick-add-card');
    if (quickAdd) {
      quickAdd.dataset.variantId = selectElement.value;
    }
  }

  subscribeVariantChange() {
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this._unsubVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data && data.variant) {
          this.syncAddonVariants(data.variant);
        }
      });
    }
  }

  syncAddonVariants(mainVariant) {
    const mainOptions = [];
    if (mainVariant.option1) mainOptions.push(mainVariant.option1);
    if (mainVariant.option2) mainOptions.push(mainVariant.option2);
    if (mainVariant.option3) mainOptions.push(mainVariant.option3);
    if (mainOptions.length === 0 && mainVariant.title) {
      mainOptions.push(...mainVariant.title.split(' / ').map(s => s.trim()));
    }

    this.querySelectorAll('.addon-card__variant-select').forEach(select => {
      const matchedOption = this.findMatchingOption(select, mainOptions);
      if (matchedOption && !matchedOption.disabled) {
        select.value = matchedOption.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  findMatchingOption(select, mainOptions) {
    const options = Array.from(select.options).filter(o => !o.disabled);
    if (options.length === 0) return null;

    const normalize = s => s.replace(/[^a-z0-9]/gi, '').toLowerCase();

    let bestOption = null;
    let bestScore = -1;

    options.forEach(opt => {
      const optValues = opt.text.split(' / ').map(s => s.trim());
      let score = 0;

      const len = Math.max(mainOptions.length, optValues.length);
      for (let i = 0; i < len; i++) {
        const main = mainOptions[i];
        const addon = optValues[i];
        if (!main || !addon) continue;

        const mainLower = main.toLowerCase();
        const addonLower = addon.toLowerCase();

        if (mainLower === addonLower) {
          score += 3;
        } else if (mainLower.includes(addonLower) || addonLower.includes(mainLower)) {
          score += 2;
        } else if (normalize(main) === normalize(addon) ||
                   normalize(main).includes(normalize(addon)) ||
                   normalize(addon).includes(normalize(main))) {
          score += 1;
        }
      }

      if (score > bestScore) {
        bestScore = score;
        bestOption = opt;
      }
    });

    return bestScore > 0 ? bestOption : null;
  }

  updatePrice(selectElement) {
    const selectedOption = selectElement.options[selectElement.selectedIndex];
    const price = selectedOption.dataset.price;
    const comparePrice = selectedOption.dataset.comparePrice;
    const currencyFormat = window.theme?.settings?.money_with_currency_format || '${{amount}}';
    const card = selectElement.closest('.addon-card');
    const priceContainer = card.querySelector('.addon-card__price');

    if (price && priceContainer) {
      const formattedPrice = formatMoney(price, currencyFormat);
      const hasSale = comparePrice && parseInt(comparePrice) > parseInt(price);

      if (hasSale) {
        const formattedComparePrice = formatMoney(comparePrice, currencyFormat);
        priceContainer.innerHTML = `
          <span class="addon-card__price-sale" data-product-price="${price}">${formattedPrice}</span>
          <s class="addon-card__price-compare" data-product-compare-price="${comparePrice}">${formattedComparePrice}</s>
        `;
      } else {
        priceContainer.innerHTML = `
          <span class="addon-card__price-regular" data-product-price="${price}">${formattedPrice}</span>
        `;
      }
    }
  }

  updateImage(selectElement) {
    const selectedOption = selectElement.options[selectElement.selectedIndex];
    const variantId = selectedOption.value;
    const productId = selectElement.dataset.productId;
    const card = selectElement.closest('.addon-card');

    if (this.variantImages[productId] && this.variantImages[productId][variantId]) {
      const imageData = this.variantImages[productId][variantId];
      const images = card.querySelectorAll('.addon-img');
      images.forEach(img => {
        img.src = imageData.url;
        img.alt = imageData.alt;
        if (img.hasAttribute('srcset')) {
          const srcset = imageData.url.replace(/\?.*$/, '');
          img.srcset = `${srcset}?width=120 120w, ${srcset}?width=240 240w`;
        }
      });
    }
  }

  getSelectedProducts() {
    const selected = [];

    this.querySelectorAll('.addon-card__checkbox:checked').forEach(checkbox => {
      const productId = checkbox.dataset.productId;
      const card = checkbox.closest('.addon-card');
      const variantSelect = card.querySelector('.addon-card__variant-select');
      let variantId;

      if (variantSelect) {
        variantId = variantSelect.value;
      } else {
        const hiddenInput = card.querySelector('.selected-variant-id');
        variantId = hiddenInput ? hiddenInput.value : null;
      }

      if (variantId) {
        selected.push({
          productId: productId,
          variantId: variantId
        });
      }
    });

    return selected;
  }
}

/* ─── Quick Add Card: standalone add-to-cart button ─── */
class QuickAddCard extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('.addon-card__add-btn');
  }

  connectedCallback() {
    if (this.button) {
      this.button.addEventListener('click', this.handleClick.bind(this));
    }
  }

  handleClick(e) {
    e.preventDefault();
    e.stopPropagation();

    const variantId = this.resolveVariantId();
    if (!variantId) return;

    this.classList.add('is-loading');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    if (cart) {
      cart.getSectionsToRender().forEach(s => formData.append('sections[]', s.id));
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement(document.activeElement);
    }

    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then(res => res.json())
      .then(response => {
        this.classList.remove('is-loading');

        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'quick-add-card',
            productVariantId: variantId,
            errors: response.errors || response.description,
            message: response.message,
          });
          return;
        }

        this.dispatchEvent(new CustomEvent('addon:quick_add', {
          bubbles: true,
          detail: {
            productId: this.dataset.productId,
            variantId: String(variantId),
            productTitle: this.closest('.addon-card')?.querySelector('.addon-card__title')?.textContent?.trim(),
            price: response.items?.[0]?.price ? (response.items[0].price / 100).toFixed(2) : null,
            category: this.closest('[data-category]')?.dataset?.category,
          },
        }));

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'quick-add-card',
          productVariantId: variantId,
          cartData: response,
        });

        if (cart) {
          cart.renderContents(response);
        }

        // Show success state briefly
        this.classList.add('is-success');
        setTimeout(() => this.classList.remove('is-success'), 1200);
      })
      .catch(() => {
        this.classList.remove('is-loading');
      });
  }

  resolveVariantId() {
    // Prefer the card's select if it exists (user may have changed variant)
    const card = this.closest('.addon-card');
    if (card) {
      const select = card.querySelector('.addon-card__variant-select');
      if (select) return select.value;
      const hidden = card.querySelector('.selected-variant-id');
      if (hidden) return hidden.value;
    }
    return this.dataset.variantId;
  }
}

if (!customElements.get('upsell-add-on')) {
  customElements.define('upsell-add-on', UpsellAddOn);
}

if (!customElements.get('quick-add-card')) {
  customElements.define('quick-add-card', QuickAddCard);
}
